import { fmtDate } from '../lib/format';
import React from 'react';
import { Users, Building2, Calendar, AlertCircle, RefreshCw, Briefcase, GraduationCap, Stethoscope, ClipboardCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useFetch, Spinner } from '../lib/useFetch';
import { useNavigate } from 'react-router-dom';
import { dashboardApi, calendarApi, companiesApi, workersApi } from '../lib/api';
import UpcomingExpirationsWidget from '../components/UpcomingExpirationsWidget';

const MACRO_LABELS = {
  ristorazione: 'Ristorazione', ricettivo: 'Ricettivo', artigiano: 'Artigiano',
  associazione: 'Associazioni', metalmeccanico: 'Metalmeccanico', servizi: 'Servizi',
  commercio: 'Commercio', edilizia: 'Edilizia', agricoltura: 'Agricoltura',
  industria: 'Industria', sanita: 'Sanità', trasporti: 'Trasporti', altro: 'Altro',
  non_definito: 'Non definito',
};

const MACRO_COLORS = ['#023F80', '#15803d', '#f59e0b', '#9333ea', '#0891b2', '#b45309', '#dc2626', '#0d9488', '#65a30d', '#e11d48', '#7c3aed', '#0ea5e9', '#64748b', '#cbd5e1'];

// Donut chart with legend. Each segment has hover tooltip via <title>.
const Donut = ({ data, size = 180, hole = 0.55, onSliceClick }) => {
  const total = data.reduce((a, d) => a + (d.value || 0), 0);
  if (total === 0) {
    return (
      <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
        <svg viewBox={`0 0 ${size} ${size}`} className="block" style={{ width: size, height: size }}>
          <circle cx={size / 2} cy={size / 2} r={size / 2 - 1} fill="#f1f5f9" />
          <circle cx={size / 2} cy={size / 2} r={(size * hole) / 2} fill="#fff" />
        </svg>
        <span className="absolute text-[10px] uppercase tracking-widest font-semibold text-slate-400">Nessun dato</span>
      </div>
    );
  }
  const r = size / 2;
  const ri = r * hole;
  let acc = 0;
  const segs = data.map((d, i) => {
    const v = d.value || 0;
    if (v === 0) return null;
    const start = (acc / total) * 2 * Math.PI;
    acc += v;
    const end = (acc / total) * 2 * Math.PI;
    const large = end - start > Math.PI ? 1 : 0;
    const x1 = r + r * Math.sin(start), y1 = r - r * Math.cos(start);
    const x2 = r + r * Math.sin(end),   y2 = r - r * Math.cos(end);
    const x3 = r + ri * Math.sin(end),  y3 = r - ri * Math.cos(end);
    const x4 = r + ri * Math.sin(start),y4 = r - ri * Math.cos(start);
    const pct = ((v / total) * 100).toFixed(1);
    const clickable = !!onSliceClick && d.key;
    return (
      <path
        key={i}
        d={`M${x1},${y1} A${r},${r} 0 ${large} 1 ${x2},${y2} L${x3},${y3} A${ri},${ri} 0 ${large} 0 ${x4},${y4} Z`}
        fill={d.color}
        stroke="#fff"
        strokeWidth="2"
        style={clickable ? { cursor: 'pointer' } : undefined}
        onClick={clickable ? () => onSliceClick(d.key) : undefined}
        data-testid={clickable ? `chart-slice-${d.key}` : undefined}
      >
        <title>{`${d.label}: ${v} (${pct}%)`}</title>
      </path>
    );
  });
  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="block" style={{ width: size, height: size }}>
      {segs}
      <text x={r} y={r - 4} textAnchor="middle" fontSize="22" fontWeight="800" fill="#023F80">{total}</text>
      <text x={r} y={r + 16} textAnchor="middle" fontSize="10" fontWeight="600" fill="#64748b">TOTALE</text>
    </svg>
  );
};

const PieCard = ({ title, icon: Icon, data, testId, onSliceClick }) => {
  const totalNonZero = data.filter((d) => d.value > 0);
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 min-h-[230px]" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-gray-800 text-sm flex items-center gap-2">
          {Icon && <Icon size={16} className="text-[#023F80]" />} {title}
        </h3>
      </div>
      <div className="flex items-center gap-5">
        <div className="flex-shrink-0"><Donut data={data} onSliceClick={onSliceClick} /></div>
        <ul className="space-y-1.5 text-xs flex-1 min-w-0">
          {(totalNonZero.length ? data : []).map((d) => {
            const total = data.reduce((a, x) => a + x.value, 0) || 1;
            const pct = ((d.value / total) * 100).toFixed(1);
            const clickable = !!onSliceClick && d.key;
            return (
              <li
                key={d.label}
                className={`flex items-center gap-2 min-w-0 ${clickable ? 'cursor-pointer hover:bg-slate-50 rounded px-1 -mx-1' : ''}`}
                onClick={clickable ? () => onSliceClick(d.key) : undefined}
                data-testid={clickable ? `chart-legend-${d.key}` : undefined}
              >
                <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: d.color }} />
                <span className="font-semibold text-slate-700 truncate flex-1" title={d.label}>{d.label}</span>
                <span className="font-bold text-slate-900 tabular-nums">{d.value}</span>
                <span className="text-slate-400 text-[10px] tabular-nums w-10 text-right">{pct}%</span>
              </li>
            );
          })}
          {totalNonZero.length === 0 && <li className="text-slate-400 italic">Nessun dato disponibile</li>}
        </ul>
      </div>
    </div>
  );
};

const DashboardPage = () => {
  const { user } = useAuth();
  const isClient = user?.role === 'client';

  const { data: stats, loading } = useFetch(() => dashboardApi.stats(), []);
  const { data: events } = useFetch(() => calendarApi.list(), []);
  const { data: companies } = useFetch(() => companiesApi.list(), []);
  const { data: workers } = useFetch(() => workersApi.list(), []);
  const { data: charts } = useFetch(() => dashboardApi.charts(), []);
  const navigate = useNavigate();

  const onMacroClick = React.useCallback((macroKey) => {
    if (!macroKey) return;
    navigate(`/aziende?macrosettore=${encodeURIComponent(macroKey)}`);
  }, [navigate]);

  if (loading || !stats) return <Spinner label="Caricamento dashboard..." />;

  const c = charts || {};
  const _b = (obj) => ({
    total: obj?.total || 0,
    scaduto: obj?.scaduto || 0,
    in_scadenza: obj?.in_scadenza || 0,
    valido: obj?.valido || 0,
    missing: obj?.missing || 0,
  });
  const cF = _b(c.formazione);
  const cV = _b(c.visite);
  const cSL = c.sopralluoghi || { total: 0, fatti: 0, da_fare: 0, scaduti: 0 };
  const macrosettori = c.macrosettori || {};
  const aziendeAnno = c.aziende_anno || { nuove: 0, dismesse: 0, year: new Date().getFullYear() };

  // Build pie datasets — keep the raw macrosettore key inside each row so
  // clicks can navigate to /aziende?macrosettore=<key>.
  const macroData = Object.entries(macrosettori)
    .map(([k, v], i) => ({ key: k, label: MACRO_LABELS[k] || k, value: v, color: MACRO_COLORS[i % MACRO_COLORS.length] }))
    .sort((a, b) => b.value - a.value);

  const formData = [
    { label: 'Validi', value: cF.valido, color: '#10b981' },
    { label: 'In scadenza (30gg)', value: cF.in_scadenza, color: '#f59e0b' },
    { label: 'Scaduti', value: cF.scaduto, color: '#ef4444' },
  ];

  const visiteData = [
    { label: 'Valide', value: cV.valido, color: '#10b981' },
    { label: 'In scadenza (30gg)', value: cV.in_scadenza, color: '#f59e0b' },
    { label: 'Scadute', value: cV.scaduto, color: '#ef4444' },
  ];

  const sopralluoghiData = [
    { label: 'Fatti (storico)', value: cSL.fatti, color: '#10b981' },
    { label: 'Da fare', value: cSL.da_fare, color: '#023F80' },
    { label: 'Scaduti', value: cSL.scaduti, color: '#ef4444' },
  ];

  const aziendeAnnoData = [
    { label: `Nuove ${aziendeAnno.year}`, value: aziendeAnno.nuove, color: '#10b981' },
    { label: `Dismesse ${aziendeAnno.year}`, value: aziendeAnno.dismesse, color: '#ef4444' },
  ];

  const totalCompanies = companies?.length || 0;
  const totalScaduti = cF.scaduto + cV.scaduto + (cSL.scaduti || 0);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-semibold text-gray-500 mb-1.5">Ricerca per azienda</div>
          <Select onValueChange={(v) => v && navigate(`/aziende/${v}`)}>
            <SelectTrigger data-testid="filter-company" className="h-11 rounded-xl bg-white border-gray-200"><SelectValue placeholder="Seleziona azienda..." /></SelectTrigger>
            <SelectContent className="max-h-80">
              {(companies || []).filter(c => c.id).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-semibold text-gray-500 mb-1.5">Ricerca per sede</div>
          <Select onValueChange={(v) => v && navigate(`/aziende?sede=${encodeURIComponent(v)}`)}>
            <SelectTrigger data-testid="filter-sede" className="h-11 rounded-xl bg-white border-gray-200"><SelectValue placeholder="Seleziona sede..." /></SelectTrigger>
            <SelectContent className="max-h-80">{Array.from(new Set((companies || []).map((c) => c.sede))).filter(Boolean).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-semibold text-gray-500 mb-1.5">Ricerca per lavoratore</div>
          <Select onValueChange={(v) => v && navigate(`/lavoratori/${v}`)}>
            <SelectTrigger data-testid="filter-worker" className="h-11 rounded-xl bg-white border-gray-200"><SelectValue placeholder="Seleziona lavoratore..." /></SelectTrigger>
            <SelectContent className="max-h-80">{(workers || []).filter(w => w.id).map((w) => <SelectItem key={w.id} value={w.id}>{w.firstName} {w.lastName}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {/* Quick KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiBig label="Aziende monitorate" value={totalCompanies} bg="#e0e7ff" color="#023F80" icon={Building2} />
        <KpiBig label="Lavoratori" value={stats.workers || 0} bg="#dcfce7" color="#15803d" icon={Users} />
        <KpiBig label="Sopralluoghi" value={cSL.total} bg="#fef7d4" color="#8a6608" icon={ClipboardCheck} />
        <KpiBig label="Scadenze critiche" value={totalScaduti} bg="#fee2e2" color="#b91c1c" icon={AlertCircle} />
      </div>

      <Tabs defaultValue="grafici" className="w-full">
        <div className="flex justify-center">
          <TabsList className="bg-transparent gap-2">
            <TabsTrigger value="grafici" className="rounded-full px-6 py-2 data-[state=active]:bg-[#023F80] data-[state=active]:text-white data-[state=inactive]:bg-white data-[state=inactive]:text-gray-700 data-[state=inactive]:border data-[state=inactive]:border-gray-200 text-xs font-semibold tracking-wider">GRAFICI</TabsTrigger>
            <TabsTrigger value="logs" className="rounded-full px-6 py-2 data-[state=active]:bg-[#023F80] data-[state=active]:text-white data-[state=inactive]:bg-white data-[state=inactive]:text-gray-700 data-[state=inactive]:border data-[state=inactive]:border-gray-200 text-xs font-semibold tracking-wider">LOGS</TabsTrigger>
            <TabsTrigger value="contratti" className="rounded-full px-6 py-2 data-[state=active]:bg-[#023F80] data-[state=active]:text-white data-[state=inactive]:bg-white data-[state=inactive]:text-gray-700 data-[state=inactive]:border data-[state=inactive]:border-gray-200 text-xs font-semibold tracking-wider">CONTRATTI</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="grafici" className="mt-6 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {!isClient && (
              <PieCard
                title="Settori delle aziende"
                icon={Building2}
                data={macroData}
                testId="chart-macrosettori"
                onSliceClick={onMacroClick}
              />
            )}
            <PieCard
              title="Corsi di formazione"
              icon={GraduationCap}
              data={formData}
              testId="chart-formazione"
            />
            <PieCard
              title="Visite mediche"
              icon={Stethoscope}
              data={visiteData}
              testId="chart-visite"
            />
            <PieCard
              title="Sopralluoghi"
              icon={ClipboardCheck}
              data={sopralluoghiData}
              testId="chart-sopralluoghi"
            />
            {!isClient && (
              <PieCard
                title={`Aziende — anno ${aziendeAnno.year}`}
                icon={Briefcase}
                data={aziendeAnnoData}
                testId="chart-aziende-anno"
              />
            )}
          </div>

          <UpcomingExpirationsWidget />

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-center justify-between mb-3"><h3 className="font-bold text-gray-800">Calendario eventi</h3><button className="text-xs text-gray-500 hover:text-[#023F80]">Vedi tutte</button></div>
            <div className="divide-y divide-gray-100">
              {(events || []).slice(0, 6).map((s) => (
                <div key={s.id} className="py-2.5 flex items-center gap-3 text-sm">
                  <div className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                  <div className="flex-1">
                    <div className="font-medium text-gray-800">{s.title}</div>
                    <div className="text-xs text-gray-500 uppercase">{s.type}</div>
                  </div>
                  <div className="text-xs font-semibold text-gray-700">{fmtDate(s.date)}</div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="logs" className="mt-6">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <h3 className="font-bold text-gray-800 mb-3">Log attività recenti</h3>
            <ul className="space-y-2 text-sm">
              <li className="flex justify-between border-b border-gray-100 py-2"><span>Login utente {user?.username}</span><span className="text-gray-500">poco fa</span></li>
              <li className="flex justify-between border-b border-gray-100 py-2"><span>Dashboard caricata</span><span className="text-gray-500">{new Date().toLocaleTimeString('it-IT')}</span></li>
            </ul>
          </div>
        </TabsContent>

        <TabsContent value="contratti" className="mt-6">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <h3 className="font-bold text-gray-800 mb-3">Contratti attivi</h3>
            <p className="text-sm text-gray-500">{totalCompanies} aziende monitorate</p>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

const KpiBig = ({ label, value, bg, color, icon: Icon }) => (
  <div className="relative rounded-2xl p-4 overflow-hidden" style={{ background: bg }}>
    <div className="text-[10px] uppercase tracking-widest font-semibold text-gray-700">{label}</div>
    <div className="mt-1 text-3xl font-extrabold tabular-nums" style={{ color }}>{value}</div>
    {Icon && <Icon size={56} className="absolute right-3 bottom-1 opacity-15" style={{ color }} />}
  </div>
);

export default DashboardPage;
